import { ApolloError } from '@apollo/client';

import { getFieldErrors } from '@utils/graphqlError';

import type { FormValues } from './ProjectFormStack';

// TA-10140: maps backend field_errors keys (snake_case) onto this form's
// Formik field names (camelCase). Kept in its own module (rather than
// inline in ProjectFormStack.tsx) so it can be unit-tested without pulling
// in that screen's heavy transitive imports (navigation stacks, native
// modules, etc.) — see TaskFormStack's analogous taskFormSubmitHandler.ts.
const PROJECT_FORM_FIELD_NAME_MAP: Record<string, keyof FormValues> = {
  name: 'name',
  team_id: 'teamId',
};

export const getProjectFormFieldErrors = (
  error: ApolloError
): Partial<Record<keyof FormValues, string>> => {
  const fieldErrors = getFieldErrors(error);
  const result: Partial<Record<keyof FormValues, string>> = {};
  Object.entries(fieldErrors).forEach(([backendField, messages]) => {
    const formikField = PROJECT_FORM_FIELD_NAME_MAP[backendField];
    if (formikField && messages?.[0]) {
      result[formikField] = messages[0];
    }
  });
  return result;
};

// Bug fix (code review): a backend field_errors key with no entry in
// PROJECT_FORM_FIELD_NAME_MAP was silently dropped — the user saw no
// feedback at all. Only true when field_errors actually came back but some
// of them didn't map onto a Formik field, so the pre-existing
// storageLimitReached alert (a message-only error with no field_errors)
// isn't double-fired alongside this one.
export const shouldShowProjectFormFallbackAlert = (
  error: ApolloError
): boolean => {
  const backendFieldCount = Object.keys(getFieldErrors(error)).length;
  const mappedFieldCount = Object.keys(getProjectFormFieldErrors(error)).length;
  return backendFieldCount > 0 && mappedFieldCount < backendFieldCount;
};
